import type { ShipmentKind } from "./api/types";

/**
 * Supply Chain permission keys (catalog `scm.*`, docs/contracts/supply-chain.md §9). International shipments keep the
 * legacy menu 256, local shipments menu 257; the UI hides what a key does not allow and the API checks the same keys.
 */
export const scmPermissions = {
  moduleView: "scm.supply_chain.view",
  shipmentView: "scm.international_shipments.view",
  shipmentCreate: "scm.international_shipments.create",
  shipmentUpdate: "scm.international_shipments.update",
  shipmentPost: "scm.international_shipments.post",
  shipmentDelete: "scm.international_shipments.delete",
  shipmentPrint: "scm.international_shipments.print",
  shipmentHistory: "scm.international_shipments.history",
  localView: "scm.local_shipments.view",
  localCreate: "scm.local_shipments.create",
  localUpdate: "scm.local_shipments.update",
  localPost: "scm.local_shipments.post",
  localDelete: "scm.local_shipments.delete",
  localPrint: "scm.local_shipments.print",
  localHistory: "scm.local_shipments.history",
  receive: "scm.shipment.receive",
  handover: "scm.shipment.handover",
  cancel: "scm.shipment.cancel",
  trackingView: "scm.container_tracking.view",
  trackingRefresh: "scm.container_tracking.update",
  tariffQuote: "scm.tariff_quote.view",
  forecastView: "scm.demand_forecasts.view",
  forecastCreate: "scm.demand_forecasts.create",
  forecastUpdate: "scm.demand_forecasts.update",
  forecastApprove: "scm.demand_forecasts.post",
  forecastDelete: "scm.demand_forecasts.delete",
  revisionView: "scm.forecast_revisions.view",
  revisionUpdate: "scm.forecast_revisions.update",
  planView: "scm.stock_plans.view",
  planCreate: "scm.stock_plans.create",
  planUpdate: "scm.stock_plans.update",
  planRelease: "scm.stock_plans.post",
  planDelete: "scm.stock_plans.delete",
  airwayView: "scm.airway_bill.view",
  airwayCreate: "scm.airway_bill.create",
  airwayUpdate: "scm.airway_bill.update",
  airwayInvoice: "scm.airway_bill.post",
  airwayVoid: "scm.airway_bill.delete",
  loadingView: "scm.loading_list.view",
  loadingCreate: "scm.loading_list.create",
  loadingUpdate: "scm.loading_list.update",
  loadingPost: "scm.loading_list.post",
  loadingDelete: "scm.loading_list.delete",
  loadingPrint: "scm.loading_list.print",
  logisticsView: "scm.document_logistics.view",
  logisticsUpdate: "scm.document_logistics.update",
  referenceImport: "scm.reference_import.post",
  settingsView: "scm.settings.view",
  settingsUpdate: "scm.settings.update",
} as const;

/** The keys of one shipment kind (view, create, update, post, delete, print). */
export function shipmentKeys(kind: ShipmentKind) {
  const local = kind === "local";
  return {
    view: local ? scmPermissions.localView : scmPermissions.shipmentView,
    create: local ? scmPermissions.localCreate : scmPermissions.shipmentCreate,
    update: local ? scmPermissions.localUpdate : scmPermissions.shipmentUpdate,
    post: local ? scmPermissions.localPost : scmPermissions.shipmentPost,
    delete: local ? scmPermissions.localDelete : scmPermissions.shipmentDelete,
    print: local ? scmPermissions.localPrint : scmPermissions.shipmentPrint,
  };
}

/** Plan module of Supply Chain (P6). */
export const scmModule = "supply_chain";

/** The supply chain settings group (`/platform/settings/scm`). */
export const scmSettingsGroup = "scm";
